<template>
    <RadioGroupRoot :orientation="orientation" :disabled="disabled" :aria-labelledby="label ? labelId : undefined" class="radio-group">
        <span v-if="label" :id="labelId" class="group-label">{{ label }}</span>
        <label v-for="option of options" :key="String(option.value)" :for="itemIds[String(option.value)]" class="option" :class="{ 'is-disabled': option.disabled || disabled }">
            <RadioGroupItem :id="itemIds[String(option.value)]" :value="option.value" :disabled="option.disabled" class="radio">
                <RadioGroupIndicator class="indicator" />
            </RadioGroupItem>
            <span class="option-label">{{ option.label }}</span>
        </label>
    </RadioGroupRoot>
</template>

<script setup lang="ts">
import { type AcceptableValue, RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from "reka-ui";
import { computed, useId } from "vue";

export type RadioGroupOrientation = "vertical" | "horizontal";

export type RadioGroupOption = {
    value: AcceptableValue;
    label: string;
    disabled?: boolean;
};

type BaseRadioGroupProps = {
    options: RadioGroupOption[];
    label?: string;
    orientation?: RadioGroupOrientation;
    disabled?: boolean;
};

const { options, label, orientation = "vertical", disabled = false } = defineProps<BaseRadioGroupProps>();

const labelId = useId();

const itemIds = computed<Record<string, string>>(() => Object.fromEntries(options.map((option) => [String(option.value), `${labelId}-${String(option.value)}`])));
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .radio-group {
        @apply flex flex-col gap-3 aria-[orientation=horizontal]:flex-row aria-[orientation=horizontal]:flex-wrap;
    }

    .group-label {
        @apply body-small-bold text-control-label;
    }

    .option {
        @apply flex flex-row items-center gap-3;
    }

    .option.is-disabled {
        @apply cursor-not-allowed;
    }

    .radio {
        @apply flex h-5 min-w-5 max-w-5 shrink-0 appearance-none items-center justify-center rounded-full border border-control-border-strong bg-control-bg transition-colors data-[state=checked]:border-control-accent data-disabled:cursor-not-allowed data-disabled:opacity-50;
    }

    .indicator {
        @apply flex h-full w-full items-center justify-center after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-control-accent after:content-[''];
    }

    .option-label {
        @apply body select-none text-control-label;
    }

    .option.is-disabled .option-label {
        @apply text-control-label-disabled;
    }
}
</style>
